import { useContext } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { datadogRum } from '@datadog/browser-rum'
import { QueryClientContext } from '@tanstack/react-query'
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary'

import { isDatadogRUMEnabled } from '@/lib/datadog'

const ErrorContainer = styled(Box, {
	shouldForwardProp: (p) => p !== 'fullPage',
})<{ fullPage?: boolean }>(({ theme, fullPage }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	minHeight: fullPage ? '100vh' : 'auto',
	gap: theme.spacing(2),
	padding: theme.spacing(3),
}))

const DevErrorBox = styled(Box)(({ theme }) => ({
	marginTop: theme.spacing(2),
	padding: theme.spacing(2),
	backgroundColor: theme.palette.grey[100],
	borderRadius: theme.shape.borderRadius,
	fontFamily: 'monospace',
	fontSize: 12,
	maxWidth: '100%',
	overflow: 'auto',
	whiteSpace: 'pre-wrap',
}))

interface ErrorFallbackProps extends FallbackProps {
	fullPage?: boolean
}

function ErrorFallback({ error, resetErrorBoundary, fullPage }: ErrorFallbackProps) {
	const queryClient = useContext(QueryClientContext)

	function handleReset() {
		if (queryClient) void queryClient.resetQueries()
		resetErrorBoundary()
	}

	return (
		<ErrorContainer fullPage={fullPage}>
			<Typography variant="h5" sx={{ fontWeight: 'bold' }}>
				Something went wrong
			</Typography>
			<Typography
				variant="body1"
				color="text.secondary"
				sx={{ textAlign: 'center', maxWidth: 480 }}
			>
				Please refresh the page. If the problem persists, contact support.
			</Typography>
			<Button variant="contained" onClick={handleReset}>
				Try again
			</Button>
			{import.meta.env.DEV && <DevErrorBox>{String(error)}</DevErrorBox>}
		</ErrorContainer>
	)
}

interface ErrorBoundaryProps {
	children: React.ReactNode
	fullPage?: boolean
}

export function ErrorBoundary({ children, fullPage }: ErrorBoundaryProps) {
	return (
		<ReactErrorBoundary
			fallbackRender={({ error, resetErrorBoundary }) => (
				<ErrorFallback
					error={error}
					resetErrorBoundary={resetErrorBoundary}
					fullPage={fullPage}
				/>
			)}
			onError={(error, info) => {
				if (isDatadogRUMEnabled()) {
					datadogRum.addError(error, { componentStack: info.componentStack })
				}
			}}
		>
			{children}
		</ReactErrorBoundary>
	)
}
